import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, UtensilsCrossed, Droplets, Waves, Plus, Settings, AlertTriangle, CheckCircle2, Clock, FileWarning, X, Eye } from 'lucide-react';
import { useFetch, Spinner } from '../lib/useFetch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { registriesApi, companiesApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import RegistryEntryDialog from '../components/RegistryEntryDialog';
import { fmtDate } from '../lib/format';
import { toast } from 'sonner';
import { downloadFile } from '../lib/api';
import { FileDown } from 'lucide-react';

const MACRO_META = {
  haccp:      { label: 'HACCP', color: '#8a6608', bg: '#fef7d4', icon: UtensilsCrossed },
  legionella: { label: 'Legionella', color: '#155e75', bg: '#cffafe', icon: Droplets },
  piscine:    { label: 'Piscine', color: '#166534', bg: '#dcfce7', icon: Waves },
};

const RegistriPage = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const navigate = useNavigate();
  const [selectedCompany, setSelectedCompany] = useState(isClient ? user?.company : '');
  const [tab, setTab] = useState('haccp');
  const [entryFor, setEntryFor] = useState(null); // template object
  const [assignmentMode, setAssignmentMode] = useState(false);

  const { data: companies } = useFetch(() => companiesApi.list(), []);
  const { data: templates } = useFetch(() => registriesApi.templates(), []);
  const { data: dashboard, reload: reloadDash } = useFetch(
    () => selectedCompany ? registriesApi.dashboard({ companyId: selectedCompany }) : Promise.resolve({ data: null }),
    [selectedCompany],
  );
  const { data: assignments, reload: reloadA } = useFetch(
    () => selectedCompany ? registriesApi.companyAssignments(selectedCompany) : Promise.resolve({ data: [] }),
    [selectedCompany],
  );
  const { data: entries, reload: reloadE } = useFetch(
    () => selectedCompany ? registriesApi.entries({ companyId: selectedCompany, limit: 200 }) : Promise.resolve({ data: [] }),
    [selectedCompany],
  );

  // Default-select first company for consultants
  useEffect(() => {
    if (!isClient && !selectedCompany && companies?.length) {
      const first = companies.find((c) => c.id);
      if (first) setSelectedCompany(first.id);
    }
  }, [companies, selectedCompany, isClient]);

  const company = useMemo(() => companies?.find((c) => c.id === selectedCompany), [companies, selectedCompany]);

  const activeAssignments = useMemo(() => (assignments || []).filter((a) => a.active), [assignments]);
  const assignedTemplateIds = useMemo(() => new Set(activeAssignments.map((a) => a.templateId)), [activeAssignments]);

  const templatesByArea = useMemo(() => {
    const out = { haccp: [], legionella: [], piscine: [] };
    (templates || []).forEach((t) => {
      if (out[t.macroArea]) out[t.macroArea].push(t);
    });
    return out;
  }, [templates]);

  const activeByArea = useMemo(() => {
    const out = { haccp: [], legionella: [], piscine: [] };
    (templates || []).forEach((t) => {
      if (assignedTemplateIds.has(t.id) && out[t.macroArea]) out[t.macroArea].push(t);
    });
    return out;
  }, [templates, assignedTemplateIds]);

  const lastEntryByTpl = useMemo(() => {
    const out = {};
    (entries || []).forEach((e) => {
      const cur = out[e.templateId];
      if (!cur || (e.timestamp || '') > (cur.timestamp || '')) out[e.templateId] = e;
    });
    return out;
  }, [entries]);

  const toggleAssignment = async (tpl) => {
    const existing = (assignments || []).find((a) => a.templateId === tpl.id);
    try {
      if (existing) {
        if (existing.active) {
          await registriesApi.deactivateAssignment(existing.id);
          toast.success(`${tpl.label} disattivato`);
        } else {
          await registriesApi.updateAssignment(existing.id, { active: true });
          toast.success(`${tpl.label} riattivato`);
        }
      } else {
        await registriesApi.assign(selectedCompany, { templateId: tpl.id });
        toast.success(`${tpl.label} attivato`);
      }
      reloadA();
      reloadDash();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const ds = dashboard || {};

  return (
    <div className="space-y-5" data-testid="registri-page">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#e0e7ff] text-[#023F80] flex items-center justify-center"><ClipboardList size={20} /></div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Registri di autocontrollo</h2>
              <p className="text-xs text-slate-500">HACCP, Legionella, Piscine — compilazione, firma digitale, non conformità.</p>
            </div>
          </div>
          {!isClient && (
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger className="w-72 h-10 border-gray-200" data-testid="registri-company-select"><SelectValue placeholder="Seleziona azienda…" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {(companies || []).filter((c) => c.id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedCompany && (
                <button
                  onClick={async () => {
                    try {
                      await downloadFile(`/dossier/${selectedCompany}?months=12`);
                      toast.success('Download dossier ASL avviato');
                    } catch (e) {
                      toast.error('Errore generazione dossier');
                    }
                  }}
                  data-testid="registri-download-dossier"
                  className="px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold tracking-wider flex items-center gap-2"
                >
                  <FileDown size={14} className="text-[#FFCB05]" /> DOSSIER ASL (PDF)
                </button>
              )}
              <button
                onClick={() => setAssignmentMode((v) => !v)}
                data-testid="registri-toggle-assignment"
                className={`px-4 py-2 rounded-full text-xs font-bold tracking-wider flex items-center gap-2 ${assignmentMode ? 'bg-[#023F80] text-white' : 'bg-[#fef7d4] text-[#8a6608] hover:bg-[#E6B800]'}`}
              >
                <Settings size={14} /> {assignmentMode ? 'CHIUDI ATTIVAZIONI' : 'ATTIVA REGISTRI'}
              </button>
            </div>
          )}
          {isClient && selectedCompany && (
            <button
              onClick={async () => {
                try {
                  await downloadFile(`/dossier/${selectedCompany}?months=12`);
                  toast.success('Download dossier ASL avviato');
                } catch (e) {
                  toast.error('Errore generazione dossier');
                }
              }}
              data-testid="registri-download-dossier"
              className="px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold tracking-wider flex items-center gap-2"
            >
              <FileDown size={14} className="text-[#FFCB05]" /> DOSSIER ASL (PDF)
            </button>
          )}
        </div>

        {selectedCompany && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
              <Kpi testId="registri-kpi-da_compilare" label="Da compilare" value={ds.da_compilare ?? 0} bg="#fef7d4" color="#8a6608" icon={Clock} />
              <Kpi testId="registri-kpi-completati" label="Completati" value={ds.completati ?? 0} bg="#dcfce7" color="#15803d" icon={CheckCircle2} />
              <Kpi testId="registri-kpi-scaduti" label="Scaduti" value={ds.scaduti ?? 0} bg="#fee2e2" color="#b91c1c" icon={AlertTriangle} />
              <Kpi testId="registri-kpi-non_conformi" label="Non conformi" value={ds.non_conformi ?? 0} bg="#fef3c7" color="#92400e" icon={FileWarning} />
              <Kpi testId="registri-kpi-nc_aperte" label="NC aperte" value={ds.non_conformita_aperte ?? 0} bg="#e0e7ff" color="#023F80" icon={FileWarning} />
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-slate-100 p-1 flex-wrap h-auto" data-testid="registri-tabs">
                {['haccp', 'legionella', 'piscine'].map((m) => {
                  const meta = MACRO_META[m];
                  const Icon = meta.icon;
                  const totalActive = activeByArea[m]?.length || 0;
                  return (
                    <TabsTrigger key={m} value={m} data-testid={`registri-tab-${m}`} className="data-[state=active]:bg-[#023F80] data-[state=active]:text-white text-slate-700 rounded-lg px-4 py-2 text-xs font-semibold flex items-center gap-2">
                      <Icon size={14} /> {meta.label} <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/30 text-[10px]">{totalActive}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {['haccp', 'legionella', 'piscine'].map((m) => (
                <TabsContent key={m} value={m} className="mt-5">
                  {assignmentMode ? (
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Seleziona i registri da attivare per <strong className="text-[#023F80]">{company?.name}</strong></h3>
                      {templatesByArea[m].map((tpl) => {
                        const isOn = assignedTemplateIds.has(tpl.id);
                        return (
                          <label
                            key={tpl.id}
                            data-testid={`registri-toggle-${tpl.code}`}
                            className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${isOn ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 hover:bg-slate-50'}`}
                          >
                            <Checkbox checked={isOn} onCheckedChange={() => toggleAssignment(tpl)} />
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm text-slate-800">{tpl.label}</div>
                              <div className="text-xs text-slate-500">{tpl.description}</div>
                              <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">
                                Frequenza: {tpl.frequency} · {tpl.fields?.length || 0} campi
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div>
                      {activeByArea[m].length === 0 ? (
                        <div className="text-center py-10 text-sm text-slate-400">
                          Nessun registro {MACRO_META[m].label} attivato per questa azienda.
                          {!isClient && <div className="mt-2"><button onClick={() => setAssignmentMode(true)} className="text-[#023F80] font-bold hover:underline">Attiva ora →</button></div>}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {activeByArea[m].map((tpl) => {
                            const last = lastEntryByTpl[tpl.id];
                            const lastStatus = last?.conformity;
                            return (
                              <div key={tpl.id} className="border border-slate-200 rounded-xl p-4 hover:shadow-sm transition-shadow" data-testid={`registry-card-${tpl.code}`}>
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <h4 className="font-bold text-sm text-slate-800 leading-tight">{tpl.label}</h4>
                                  {lastStatus && (
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${lastStatus === 'conforme' ? 'bg-emerald-100 text-emerald-800' : lastStatus === 'non_conforme' ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'}`}>{lastStatus.replace('_', ' ')}</span>
                                  )}
                                </div>
                                <p className="text-[11px] text-slate-500 mb-3">{tpl.frequency.toUpperCase()} · {tpl.fields?.length || 0} campi</p>
                                {last ? (
                                  <p className="text-[11px] text-slate-600 mb-3">Ultima compilazione: <strong>{fmtDate(last.timestamp?.slice(0, 10))}</strong> da {last.operator?.name || '—'}</p>
                                ) : (
                                  <p className="text-[11px] text-amber-700 mb-3 italic">⚠ Mai compilato</p>
                                )}
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => setEntryFor(tpl)}
                                    data-testid={`registry-fill-${tpl.code}`}
                                    className="px-3 py-1.5 rounded-lg bg-[#023F80] hover:bg-[#1e293b] text-white text-xs font-bold flex items-center gap-1"
                                  >
                                    <Plus size={12} /> Compila
                                  </button>
                                  <button
                                    onClick={() => navigate(`/registri/${tpl.code}?company=${selectedCompany}`)}
                                    data-testid={`registry-history-${tpl.code}`}
                                    className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-semibold text-slate-700 flex items-center gap-1"
                                  >
                                    <Eye size={12} /> Storico
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </>
        )}
      </div>

      <RegistryEntryDialog
        template={entryFor}
        companyId={selectedCompany}
        open={!!entryFor}
        onOpenChange={(v) => !v && setEntryFor(null)}
        onDone={() => { reloadE(); reloadDash(); }}
      />
    </div>
  );
};

const Kpi = ({ label, value, bg, color, icon: Icon, testId }) => (
  <div className="relative rounded-xl p-3 overflow-hidden" style={{ background: bg }} data-testid={testId}>
    <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-700">{label}</div>
    <div className="mt-1 text-2xl font-extrabold tabular-nums" style={{ color }}>{value}</div>
    {Icon && <Icon size={40} className="absolute right-2 bottom-1 opacity-15" style={{ color }} />}
  </div>
);

export default RegistriPage;
